import React from 'react';
import MemberRow from './MemberRow';
import ScheduleCell from './ScheduleCell';
import { displayRole, displayCompositeRole } from './roleLabel';
import { matchesIdealWindow, matchesBoundaryWindow } from './shiftIdealMatch';
import { requiredBreakHours } from './rotaCompliance';
import './ScheduleGrid.css';

// Convert "HH:MM" time string to decimal hours
const parseTime = (timeStr) => {
  if (typeof timeStr !== 'string') return timeStr;
  const [h, m] = timeStr.split(':').map(Number);
  return h + (m || 0) / 60;
};

// Calculate total break hours for a shift
const getBreakHours = (shift, breaks) => {
  const breakEntry = breaks?.[shift.shift_id];
  if (!breakEntry || !breakEntry.length) return 0;
  return breakEntry.reduce((sum, b) => {
    const start = parseTime(b.start);
    const end = parseTime(b.end);
    return sum + (end - start);
  }, 0);
};

// Default day name → index map (Monday-first) used as a fallback when no config
// is available. Early/Late_Availability and Contractual_Days_Off store indices
// relative to the site's week start, so prefer buildDayIndex(config) below.
const DAY_INDEX = {
  monday: 0, tuesday: 1, wednesday: 2, thursday: 3,
  friday: 4, saturday: 5, sunday: 6,
};

// Build the day-name → index map for the availability arrays from the site's week
// start. The solver indexes Early/Late_Availability and Contractual_Days_Off from
// config.DAY_NAMES (e.g. Moto starts Sunday, so sunday=0); using a fixed Monday-first
// map here put the "unavailable" indicator a day out on Sunday-start sites. Falls
// back to WEEK_START_DAY rotation, then Monday-first, when DAY_NAMES is absent.
export const buildDayIndex = (config) => {
  const names = config?.DAY_NAMES;
  if (Array.isArray(names) && names.length === 7) {
    const map = {};
    names.forEach((n, i) => { map[String(n).toLowerCase()] = i; });
    return map;
  }
  const CANON = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  const start = CANON.indexOf(String(config?.WEEK_START_DAY || 'MONDAY').toLowerCase());
  const ordered = start > 0 ? [...CANON.slice(start), ...CANON.slice(0, start)] : CANON;
  const map = {};
  ordered.forEach((d, i) => { map[d] = i; });
  return map;
};

// A member is "unavailable" on a day when:
//   - the day is in Contractual_Days_Off, OR
//   - both Early_Availability and Late_Availability arrays are present and
//     neither contains that day.
// Missing/empty arrays default to "available" (no claim of unavailability).
// `dayIndex` maps day names to availability-array indices (see buildDayIndex).
export const isMemberUnavailableOnDay = (member, dayName, dayIndex = DAY_INDEX) => {
  if (!member || typeof dayName !== 'string') return false;
  const idx = dayIndex[dayName.toLowerCase()];
  if (idx === undefined) return false;
  if (Array.isArray(member.Contractual_Days_Off) && member.Contractual_Days_Off.includes(idx)) {
    return true;
  }
  const early = member.Early_Availability;
  const late = member.Late_Availability;
  const earlyArr = Array.isArray(early) ? early : null;
  const lateArr = Array.isArray(late) ? late : null;
  if (earlyArr === null && lateArr === null) return false;
  const inEarly = earlyArr ? earlyArr.includes(idx) : true;
  const inLate = lateArr ? lateArr.includes(idx) : true;
  return !inEarly && !inLate;
};

// Derive the candidate role(s) whose ideal/boundary windows a shift is checked
// against. Returns a list: for most shifts a single role, but composite pool
// shifts return both the member's role and the shift's role (OR semantics). For
// TBC/open placeholders, prefers the row's configured filter role, then parses
// shape_of_day_type with the same priority ScheduleGrid uses for open-shift groups.
export const resolveEffectiveRole = (shift, row) => {
  const sod = shift.shape_of_day_type;
  const shiftRole = shift.role && shift.role !== 'TBC' && shift.role !== 'Stint' ? shift.role : null;
  // Composite ("+"-joined) shapes are interchangeable role pools. When the assigned
  // member's own role is one of the pooled roles, check BOTH that role and the
  // backend-stamped shift.role and treat it as ideal if either matches (e.g. a Shift
  // Manager in a "Manager+Shift Manager" pool is fine against Shift Manager or Manager).
  if (typeof sod === 'string' && sod.includes('+')) {
    const parts = sod.split('+').map(p => p.trim());
    const member = row?.member;
    const memberRole =
      (member?.Role_Type && parts.includes(member.Role_Type) && member.Role_Type) ||
      (member?.Secondary_Role_Type || []).find(sec => parts.includes(sec));
    if (memberRole) return [...new Set([memberRole, shiftRole].filter(Boolean))];
  }
  if (shiftRole) return [shiftRole];
  if (row?.openFilterRole) return [row.openFilterRole];
  if (typeof sod === 'string' && sod) {
    if (sod.includes('+')) {
      const parts = sod.split('+').map(p => p.trim());
      if (parts.includes('Waiter')) return ['Waiter'];
      if (parts.includes('Kitchen')) return ['Kitchen'];
      return [parts[0]];
    }
    return [sod];
  }
  if (Array.isArray(row?.openFilterRoles) && row.openFilterRoles.length === 1) {
    return [row.openFilterRoles[0]];
  }
  return [shift.role];
};

// Resolve composite shape_of_day_type (e.g. "Manager+Waiter+Shift Manager") to a single label.
// For scheduled shifts the backend's `role` field is authoritative (it reflects the
// shift the employee is actually filling, not their person-level role).
const resolveShiftLabel = (shift, member) => {
  const sod = shift.shape_of_day_type || '';
  if (!sod.includes('+')) return sod || shift.role || '';

  const parts = sod.split('+').map(p => p.trim());

  // 1. Prefer the backend-assigned role — it already accounts for substitutes
  //    (e.g. a Shift Manager filling a Manager shift has role="Manager").
  if (shift.role && parts.includes(shift.role)) return shift.role;

  // 2. Fallback: match member's Role_Type (useful for shape-of-day demand rows
  //    that have no backend-assigned role yet).
  if (member?.Role_Type && parts.includes(member.Role_Type)) return member.Role_Type;

  // 3. Secondary roles
  const secondaryRoles = member?.Secondary_Role_Type || [];
  for (const sec of secondaryRoles) {
    if (parts.includes(sec)) return sec;
  }

  // 4. Fallback: full composite
  return sod;
};

const CURRENCY_SYMBOLS = { GBP: '£', EUR: '€', USD: '$' };

const formatForecast = (value, currency = 'GBP') => {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const sym = CURRENCY_SYMBOLS[currency] || currency || '';
  const n = Math.round(Number(value));
  return `${sym}${n.toLocaleString('en-GB')}`;
};

const ScheduleGrid = ({ tableRows, memberSchedule, orderedDays, onShiftClick, breaks, config, partnerId, shapeOfDay, forecast = null, idealMatchEnabled = true, idealMatchBuffer = 1, boundaryMatchEnabled = true, boundaryMatchBuffer = 1 }) => {
  // DONT_COUNT_BREAKS lives inside each role's BREAKS config (inherited from BASE_ROLE at resolve time)
  const dontCountBreaks = Object.values(config?.ROLES || {}).some(
    role => role?.BREAKS?.DONT_COUNT_BREAKS === true
  );
  // Week-start-aware day index for availability/days-off lookups (Moto starts Sunday).
  const dayIndexMap = buildDayIndex(config);
  return (
    <div className="schedule-grid-table">
      {/* Header row */}
      <div className="grid-header">
        <div className="grid-header__member">Team</div>
        {orderedDays.map(day => {
          const dayLower = day.toLowerCase();
          const value = forecast?.[dayLower] ?? forecast?.by_day?.[dayLower];
          const text = formatForecast(value, forecast?.currency);
          return (
            <div key={day} className="grid-header__day">
              <div>{day}</div>
              {text && (
                <div
                  className="grid-header__forecast"
                  style={{ fontSize: 12, fontWeight: 400, color: '#6b7280', marginTop: 2 }}
                >
                  {text}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Data rows */}
      {tableRows.map((row, index) => {
        if (row.type === 'header') {
          return (
            <div key={`role-${row.role}`} className="role-group-header">
              {displayRole(row.role, partnerId)}
            </div>
          );
        }

        const memberName = row.member.Name;
        const rawDays = memberSchedule[memberName] || {};

        // For open shifts (Stint/TBC), filter to only shifts matching this role group
        let memberDays = rawDays;
        if (row.isOpen && (row.openFilterRole || row.openFilterRoles)) {
          const allowedRoles = row.openFilterRoles || [row.openFilterRole];
          memberDays = {};
          Object.entries(rawDays).forEach(([day, shifts]) => {
            const filtered = shifts.filter(s => {
              if (!s.shape_of_day_type) return false;
              let resolvedRole;
              if (s.shape_of_day_type.includes('+')) {
                const parts = s.shape_of_day_type.split('+').map(p => p.trim());
                if (parts.includes('Waiter')) resolvedRole = 'Waiter';
                else if (parts.includes('Kitchen')) resolvedRole = 'Kitchen';
                else resolvedRole = parts[0];
              } else {
                resolvedRole = s.shape_of_day_type;
              }
              return allowedRoles.includes(resolvedRole);
            });
            if (filtered.length > 0) memberDays[day] = filtered;
          });
        }

        // Calculate assigned hours for this member
        const allShifts = Object.values(memberDays).flat();
        const totalShiftHours = allShifts.reduce((sum, shift) => sum + shift.length, 0);
        // Net out break hours: the actual assigned break where one exists, otherwise the
        // unpaid break the shift's length still requires (honouring the member's own
        // Break_Tiers via requiredBreakHours) so a not-yet-placed break isn't counted as
        // worked time. Take the larger of the two when both apply.
        const totalBreakHours = dontCountBreaks
          ? allShifts.reduce((sum, shift) => {
              const assigned = getBreakHours(shift, breaks);
              const required = requiredBreakHours(config, shift.role, shift.length || 0, row.member);
              return sum + Math.max(assigned, required);
            }, 0)
          : 0;
        const assignedHours = totalShiftHours - totalBreakHours;

        // Fixed = member-level flag OR role-level MATCH_EXPECTED_HOURS_EXACTLY
        const roleConfig = config?.ROLES?.[row.member?.Role_Type];
        const isFixed = row.member?.Enforce_Expected_Hours === true
          || roleConfig?.WEEKLY_HOURS?.MATCH_EXPECTED_HOURS_EXACTLY === true;

        return (
          <div key={`member-${memberName}-${row.role}-${index}`} className="grid-row">
            <MemberRow member={row.member} assignedHours={assignedHours} isFixed={isFixed} partnerId={partnerId} />
            {orderedDays.map(day => {
              const shifts = (memberDays[day] || []).map(s => ({
                ...s,
                resolvedLabel: displayCompositeRole(resolveShiftLabel(s, row.member), partnerId),
                idealMatch: idealMatchEnabled
                  ? matchesIdealWindow(
                      s,
                      row.member,
                      config,
                      shapeOfDay,
                      idealMatchBuffer,
                      resolveEffectiveRole(s, row),
                    )
                  : null,
                boundaryMatch: boundaryMatchEnabled
                  ? matchesBoundaryWindow(
                      s,
                      row.member,
                      config,
                      shapeOfDay,
                      boundaryMatchBuffer,
                      resolveEffectiveRole(s, row),
                    )
                  : null,
              }));
              const unavailable = !row.isOpen && isMemberUnavailableOnDay(row.member, day, dayIndexMap);
              return (
                <ScheduleCell
                  key={`${memberName}-${row.role}-${day}`}
                  shifts={shifts}
                  onShiftClick={onShiftClick}
                  unavailable={unavailable}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
};

export default ScheduleGrid;
